import activityFactory from '@polleverywhere/core/lib/activity_factory.js'
import { bindings } from '../'
import { createSlideSet } from './slideset.js'
import env from '@polleverywhere/core/lib/env'
import { singularize, deepDefaults } from '@polleverywhere/core/lib/util.js'
import URI from 'urijs'
import _ from 'underscore'
import fetchParams from './util/fetch_params'
import joinInstructionsParams from './util/join_instructions_params'

function getJoinInstructionsVizUrl (options = {}) {
  _.defaults(options, {
    query: {}
  })

  const uri = env
    .get('viz_url')
    .path('join_instructions')
    .protocol(env.get('secure_protocol'))

  uri.query(options.query)

  return uri.toString()
}

function getJoinInstructionsPlaceholderUrl (options = {}) {
  deepDefaults(options, {
    placeholderFormat: 'tiny',
    aspectRatio: 1.33,
    joinQuery: {
      display: 'placeholder_steps'
    }
  })

  const url = getJoinInstructionsVizUrl({
    query: options.joinQuery
  })

  const width =
    options.aspectRatio >= 1.33 && options.aspectRatio < 1.34 ? 1200 : 1600 // 4:3 aspect ratio comes out to a width/height ratio of 1.33...

  return env
    .get('slide_server_url')
    .path('placeholder_cdn_url')
    .query({
      url: url,
      width,
      height: '900',
      brand: 'polleverywhere',
      hide: 'qr_code',
      format: options.placeholderFormat
    })
    .toString()
}

function getJoinInstructionsUrl () {
  return env
    .get('api_url')
    .path('join_instructions')
    .protocol(env.get('secure_protocol'))
    .toString()
}

function mqaContainingActivity (activity) {
  if (activity.has('competition_permalink')) {
    return activityFactory({
      type: 'competition',
      permalink: activity.get('competition_permalink')
    })
  }
  // As we have more possible MQAs, add more clauses here to build the
  // MQA that contains the given activity

  return null
}

function isActivityAnMqa (activity) {
  return activity.get('type') === 'competition'

  // As we have more possible MQAs, add more clauses here to check
  // if the given activity is an MQA
}

// Given an array of PE activity urls, returns activity model and state information for
// each activity in the array. If some of the activities are part of an MQA, the enclosing
// MQA model will be in the resulting array instead of its children activities.

// The returned Promise will be resolved with an array, whose contents are objects of the form
//  {
//    activity: The model of the activity in question
//    states: An array of query objects, representing the states of the activity present in the deck.
//            Will be undefined if a state for the activity didn't need to be captured
//  {
// For example: Assume your slide deck contains the urls for the following:
// Q1 - default state (i.e no state info)
// Q2 - flow state 1
// Q2 - flow state 2
// Q2 - flow state 3
// Competition - title
// Competition - Q1 - question slide
// Competition - Q1 - choices slide
// Competition - Q1 - correctness slide
// Competition - leaderboard
//
// Then the result will be:
// [{ activity: Q1 model, states: [{}] },
//  { activity: Q2 model, states: [{ flow state 1 }, { flow state 2 }, { flow state 3 }] }
//  { activity: Competition model }]

// If you already know you're only interested in a single activity (e.g. when syncing a single
// slide). Passing that url as the second parameter will allow the function to filter for that
// activity, and will result in less network calls and a faster return.
async function fetchRelevantActivitiesForUrls (activityUrls = [], filterActivity = null) {
  // Get a grouping of activities in the deck. After this we'll have an object where the
  // keys are the path portion of the activity url (i.e. type/permalink) and the values
  // are an array with all the urls in the deck for that activity.
  let pathsAndUrls = _.groupBy(activityUrls, url => url.path())

  // If filtering for a single activity is requested, do that filter here. This way
  // we still account for all the different states of that activity.
  if (filterActivity) {
    pathsAndUrls = _.pick(pathsAndUrls, filterActivity.path())
  }

  // From an object where we have a path-to-url mapping, convert over to an array where
  // each entry is an object of the type:
  //  {
  //    activity: The model of the activity in question
  //    states: An array of query objects, representing the states of the activity present in the deck
  //  {
  const activitiesAndStates = _.keys(pathsAndUrls).map((path) => {
    const activity = activityFactory({
      type: singularize(URI(path).segment(0)),
      permalink: URI(path).segment(1)
    })
    const states = pathsAndUrls[path].map(urlInDeckWithThisPath => urlInDeckWithThisPath.query(true))
    return { activity, states }
  })

  const activityFetchPromises = []
  const activitiesInDeck = []

  const isMqaAlreadyTracked = (mqaModel) => {
    // Does any activity that we're already tracking have the same properties?
    return activitiesInDeck.some((entry) => {
      return entry.activity.get('type') === mqaModel.get('type') &&
             entry.activity.get('permalink') === mqaModel.get('permalink')
    })
  }

  const trackMqa = (mqaModel) => {
    if (!isMqaAlreadyTracked(mqaModel)) {
      activitiesInDeck.push({ activity: mqaModel })
      activityFetchPromises.push(mqaModel.fetch(fetchParams))
    }
  }

  // Now we need to do some consolidation to end up with the true list of activities
  // that are represented in the deck. For instance, if the user inserted a competition
  // and asked to sync a single MCP slide within the competition, we need to realize that
  // its the competition that contains that MCP that needs update, as opposed to just that
  // MCP itself. Unfortunately this knowledge is lost when insertion into slideware
  // happens; all we have coming from the slide notes is the url of an MCP. So we need to
  // look it up now.
  for (const currentActivityAndStates of activitiesAndStates) {
    const currentActivity = currentActivityAndStates.activity
    if (isActivityAnMqa(currentActivity)) {
      trackMqa(currentActivity)
    } else { // activity isn't an MQA, but could be a part of one
      await currentActivity.fetch(fetchParams)
      const containingMqa = mqaContainingActivity(currentActivity)
      if (containingMqa) {
        trackMqa(containingMqa)
      } else {
        activitiesInDeck.push(currentActivityAndStates)
      }
    }
  }

  // Wait until we've fetched data for all activities in the deck.
  await Promise.all(activityFetchPromises)

  return activitiesInDeck
}

function partitionUrls (urls = []) {
  const result = {
    activityUrls: [],
    nonActivityUrls: []
  }

  urls.forEach((url) => {
    if (isActivityUrl(url)) {
      result.activityUrls.push(url)
    } else {
      result.nonActivityUrls.push(url)
    }
  })

  return result
}

function isActivityUrl (url) {
  // The current assumption is that the path portion of an activity url has the
  // form /type/permalink
  return url.segment().length === 2
}

async function getSlideDataForActivitiesAndStates (activitiesAndStates = []) {
  const slideSet = await createSlideSet()
  activitiesAndStates.forEach((activityAndStates) => {
    const activity = activityAndStates.activity
    const states = activityAndStates.states
    if (states) {
      states.forEach((state) => {
        slideSet.addActivity(activity, {
          activityUrl: {
            query: Object.assign({}, state)
          },
          placeholderUrl: {
            activityQuery: Object.assign({}, state)
          }
        })
      })
    } else {
      slideSet.addActivity(activity)
    }
  })

  return slideSet.getData()
}

function createSlideDataForJoinInstructions (activityModels = []) {
  const displayParams = joinInstructionsParams(activityModels)
  return {
    title: 'Join instructions',
    activityUrl: getJoinInstructionsUrl(),
    placeholderUrl: getJoinInstructionsPlaceholderUrl({
      aspectRatio:
        bindings.masterSlideWidth() /
        bindings.masterSlideHeight(),
      joinQuery: {
        display: displayParams.join()
      }
    })
  }
}

async function getSlideDataForActivities (model) {
  if (!model) {
    throw new Error('Error retrieving slide information, no activity was provided.')
  }

  const urlToSync = new URI(model.showUrl())
  if (!isActivityUrl(urlToSync)) {
    throw new Error(`Error retrieving slide information. Url provided was not an activity url ${urlToSync}.`)
  }

  const urlsInDeck = await bindings.allActivityUrls().catch(() => { return null })
  if (!urlsInDeck) {
    throw new Error('Error retrieving slide information. No urls in presentation.')
  }

  const partitionedUrls = partitionUrls(urlsInDeck)
  const fetchedActivitiesAndStates = await fetchRelevantActivitiesForUrls(partitionedUrls.activityUrls, urlToSync)
  if (!fetchedActivitiesAndStates) {
    throw new Error('Error retrieving slide information. The activities were not found.')
  }

  let slideData = await getSlideDataForActivitiesAndStates(fetchedActivitiesAndStates)

  // Filter for what's actually in the deck. For instance, the user could've inserted a competition but
  // deleted the leaderboards (for whatever reason). We will have generated slide data for all
  // competition slides, but shouldn't spend time downloading the leaderboards' placeholders.
  slideData = slideData.filter((slideObj) => {
    return urlsInDeck.some((url) => {
      return url.equals(URI(slideObj.activityUrl))
    })
  })

  return slideData
}

/**
 * Request the slideware download a new placeholder image for the given model
 * and replace all occurrences of the placeholder in the slide of the current
 * presentation
 *
 * @param {Backbone.Model} model - activity model to sync in the presentation
 */
export async function sync (model) {
  if (!model) {
    throw new Error('Nothing to sync')
  }

  const urlToSync = new URI(model.showUrl())
  let slideData = []

  if (isActivityUrl(urlToSync)) {
    slideData = await getSlideDataForActivities(model)
  } else if (urlToSync.path() === '/join_instructions') {
    slideData = await getSlideDataForJoinInstructions()
  } else {
    throw new Error(`Error syncing slide. The url is not a known type ${urlToSync}.`)
  }

  if (!slideData || !slideData.length) {
    throw new Error(`Error syncing slide. Slide information not found for ${urlToSync}.`)
  }

  return bindings.updateAllSlides(slideData)
}

/**
 * Returns slide data for join instructions.
 * If the user only has web activities enabled, it will only show instructions for web joining
 * If the user only has sms activities enabled, it will only show instructions for sms joining
 * If the user has no activities or web & sms activities enabled, instructions for web & sms will be shown.
 */
export async function getSlideDataForJoinInstructions () {
  let activityModels = []
  const urlsInDeck = await bindings.allActivityUrls().catch(() => { return null })
  if (urlsInDeck) {
    const partitionedUrls = partitionUrls(urlsInDeck)
    const fetchedActivitiesAndStates = await fetchRelevantActivitiesForUrls(partitionedUrls.activityUrls)
    if (!fetchedActivitiesAndStates) {
      throw new Error('Error retrieving join instructions information. The activities were not found.')
    }

    activityModels = _.pluck(fetchedActivitiesAndStates, 'activity')
  }

  return createSlideDataForJoinInstructions(activityModels)
}

/**
 * Request the slideware download a new placeholder image for all activities in a presentation
 * and replace all occurrences of the placeholder on the slides of the current
 * presentation
 *
 */
export async function syncAll () {
  const urlsInDeck = await bindings.allActivityUrls()

  const partitionedUrls = partitionUrls(urlsInDeck)
  const fetchedActivitiesAndStates = await fetchRelevantActivitiesForUrls(partitionedUrls.activityUrls)
  let slideData = await getSlideDataForActivitiesAndStates(fetchedActivitiesAndStates)

  // Filter for what's actually in the deck. For instance, the user could've inserted a competition but
  // deleted the leaderboards (for whatever reason). We will have generated slide data for all
  // competition slides, but shouldn't spend time downloading the leaderboards' placeholders.
  slideData = slideData.filter((slideObj) => {
    return urlsInDeck.some(url => url.equals(URI(slideObj.activityUrl)))
  })

  // Now handle syncing of any slide that was not an activity slide
  partitionedUrls.nonActivityUrls.forEach((url) => {
    if (url.path() === '/join_instructions') {
      slideData.push(createSlideDataForJoinInstructions(_.pluck(fetchedActivitiesAndStates, 'activity')))
    }

    // Add more clauses here as we need to sync more non-activity slides
  })

  return bindings.updateAllSlides(slideData)
}
